/** Whether a page may be opened, or whether that is not yet known. */
export type LicenseStatus = "pending" | "licensed" | "unlicensed";

/** What the answer is worked out from. */
export interface LicenseInputs {
  /** Development is exempt: a test database holds the licence groups of the products it was set
   * up for, not of every page being worked on. */
  isDev: boolean;
  userName: string | undefined;
  /** The group that stands for the licence. `null` means there is none. */
  groupComments: string | null | undefined;
  /** Whether the group has been looked for yet. Until it has, nothing is known. */
  groupFetched: boolean;
  /** The hash of the database name, once it has been computed. */
  databaseHash: string | undefined;
}

/**
 * Whether a page is licensed.
 *
 * A plain comparison, so the same rule can answer for one page behind a boundary and for twenty
 * at once on the launcher without either of them keeping state to sequence it.
 *
 * "Not yet known" is a third answer rather than a refusal. Folded into "unlicensed", as it was,
 * every page reports a refusal while the groups are still on their way and a caller that draws
 * only licensed pages draws nothing at all before correcting itself.
 */
export function licenseStatusOf({
  isDev,
  userName,
  groupComments,
  groupFetched,
  databaseHash,
}: LicenseInputs): LicenseStatus {
  if (isDev) {
    return "licensed";
  }
  // Geotab's own support accounts see nothing, whatever the database says.
  if (userName?.includes("geotab")) {
    return "unlicensed";
  }
  if (userName?.endsWith("@fairfleet.de")) {
    return "licensed";
  }
  if (!groupFetched) {
    return "pending";
  }
  // No group means nobody has licensed this page for anybody.
  if (!groupComments) {
    return "unlicensed";
  }
  if (databaseHash === undefined) {
    return "pending";
  }

  return groupComments === databaseHash ? "licensed" : "unlicensed";
}
